import { findStateCodeByLabel, isKnownStateOrProvince } from '../data/locationOptions';

export type BusinessCountryForm = 'USA' | 'Canada' | 'New Zealand' | 'OTHER';

export interface ParsedGooglePlace {
  streetLine: string;
  city: string;
  stateCode: string;
  postalCode: string;
  /** Google short country code, e.g. US, CA */
  countryShort: string;
}

/** New Places API (Place) after fetchFields — uses `longText` / `shortText` on each component. */
export function parseNewGooglePlace(place: {
  addressComponents?: Array<{ types: string[]; longText?: string; shortText?: string }>;
  formattedAddress?: string;
}): ParsedGooglePlace {
  const components = place.addressComponents || [];
  const get = (type: string, useShort: boolean) => {
    const c = components.find((x) => x.types?.includes(type));
    if (!c) return '';
    const t = useShort ? c.shortText : c.longText;
    return (t ?? '').trim();
  };
  const streetNumber = get('street_number', true);
  const route = get('route', false);
  let streetLine = [streetNumber, route].filter(Boolean).join(' ').trim();
  if (!streetLine && place.formattedAddress) {
    streetLine = place.formattedAddress.split(',')[0]?.trim() || '';
  }
  const city =
    get('locality', false) ||
    get('sublocality', false) ||
    get('sublocality_level_1', false) ||
    get('administrative_area_level_2', false) ||
    '';
  const stateCode = get('administrative_area_level_1', true);
  const postalCode = get('postal_code', true);
  const countryShort = get('country', true);
  return {
    streetLine,
    city,
    stateCode,
    postalCode,
    countryShort,
  };
}

export function parseGooglePlace(place: {
  address_components?: Array<{ types: string[]; short_name: string; long_name: string }>;
  formatted_address?: string;
}): ParsedGooglePlace {
  const components = place.address_components || [];
  const get = (type: string, useShort: boolean) => {
    const c = components.find((x) => x.types.includes(type));
    if (!c) return '';
    return useShort ? c.short_name : c.long_name;
  };
  const streetNumber = get('street_number', true);
  const route = get('route', false);
  let streetLine = [streetNumber, route].filter(Boolean).join(' ').trim();
  if (!streetLine && place.formatted_address) {
    streetLine = place.formatted_address.split(',')[0]?.trim() || '';
  }
  const city =
    get('locality', false) ||
    get('sublocality', false) ||
    get('sublocality_level_1', false) ||
    get('administrative_area_level_2', false) ||
    '';
  const stateCode = get('administrative_area_level_1', true);
  const postalCode = get('postal_code', true);
  const countryShort = get('country', true);
  return {
    streetLine,
    city,
    stateCode,
    postalCode,
    countryShort,
  };
}

/** Map Google country short code to form country + optional custom label. */
export function googleCountryToBusinessForm(countryShort: string): {
  country: BusinessCountryForm;
  country_custom: string;
} {
  const u = (countryShort || '').toUpperCase();
  if (u === 'US') return { country: 'USA', country_custom: '' };
  if (u === 'CA') return { country: 'Canada', country_custom: '' };
  if (u === 'NZ') return { country: 'New Zealand', country_custom: '' };
  return { country: 'OTHER', country_custom: countryShort || '' };
}

export function normalizeBusinessCountryFromDb(country: string | null | undefined): {
  country: BusinessCountryForm;
  country_custom: string;
} {
  const raw = (country || '').trim();
  if (!raw) {
    return { country: 'USA', country_custom: '' };
  }
  if (/^usa$/i.test(raw) || raw === 'US' || /^united states(\s+of\s+america)?$/i.test(raw)) {
    return { country: 'USA', country_custom: '' };
  }
  if (/^canada$/i.test(raw) || raw === 'CA') {
    return { country: 'Canada', country_custom: '' };
  }
  if (/^new zealand$/i.test(raw) || raw === 'NZ') {
    return { country: 'New Zealand', country_custom: '' };
  }
  return { country: 'OTHER', country_custom: raw };
}

export function businessCountryFormToApi(
  country: BusinessCountryForm,
  country_custom: string | undefined
): string | undefined {
  if (country === 'OTHER') {
    const t = (country_custom || '').trim();
    return t || undefined;
  }
  return country;
}

export function normalizeStateForSubmit(
  state: string | undefined | null,
  country: BusinessCountryForm
): string | undefined {
  if (state == null) return undefined;
  const s = String(state).trim();
  if (!s) return undefined;
  if (country === 'USA') {
    if (isKnownStateOrProvince(s, 'US')) return findStateCodeByLabel(s, 'US');
    return s;
  }
  if (country === 'Canada') {
    if (isKnownStateOrProvince(s, 'CA')) return findStateCodeByLabel(s, 'CA');
    return s;
  }
  return s;
}

export function shouldUseStateSelect(country: BusinessCountryForm, state: string | undefined | null): boolean {
  if (country !== 'USA' && country !== 'Canada') return false;
  const code = country === 'USA' ? 'US' : 'CA';
  return isKnownStateOrProvince(state, code);
}
